import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import useAsyncEffect from 'use-async-effect';
import { useNavigate } from 'react-router-dom';
import { camelCase } from 'lodash';
import { useSound } from '@hooks/media';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '../../../providers';
import {
  getErrorNotification,
  useNotification,
} from '@components/Notification';
import { NOTIFICATION_TYPES } from '@components/Notification/constants';
import ROUTES from '@constants/routes';
import { useConference } from '../Dolby';
import { useHandleUEConnection } from './ueWsConnection';
import { setSessionStorageValue } from '@helpers/sessionStorage';
import EventsService from '@services/EventsService';
import FollowMeSound from '@public/sounds/followMe.mp3';
import { MytaverseLogger } from '@helpers/logger';
import {
  downloadGamecastScreenshot,
  restoringFocusDownload,
} from '@helpers/screenshotCanvas';
import {
  EFullscreenType,
  IParticipantPositionMessage,
  IWebSocketConnectionInfo,
  LastWebsocketJsonMessageType,
  PlayerViewUEEnum,
  SpatialType,
  WebsocketAction,
  WebsocketMessage,
  WebSocketUECategory,
} from '@interfaces/webSocketConnectionInfo';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { PoiMediaType, SendToBriefcasePropsTypeFull } from '../interfaces';
import { SessionStorage } from '@constants/storage';
import { IFollowerData } from '@interfaces/followers';
import { UnknownObjectType } from '@interfaces';
import { IWebsocketMessageFlag } from '../../../providers/MytaverseEventProvider/interfaces';
import { getNotification } from '@components/Notification/helpers';
import { useVirtualAssistant } from '@core/dashboard/components/HomeBottomBar/VirtualAssistant';
import { useClientSpecificTeleportVisibility } from '@hooks/clientSpecificUtils';

import { AnalyticsType } from '@interfaces/analytics';
import { useEventScheduler } from '@features/EventScheduler/context/context';
import { IRoom } from '@interfaces/rooms';
import { env } from 'env';

import { default as mytaverseLogger } from '@providers/MytaverseProvider/logger';
import ParticipantsService from '@services/ParticipantsService';
import FilesService from '@services/FilesService';
import { useMapModal } from '@core/dashboard/components/MapModal/context/context';
import { useMandatoryLogoutModal } from '@core/dashboard/components/ModalCountDown';
import { useTeleportOpen } from '@core/dashboard/components/TeleportModal/hooks/useTeleportOpen';

interface HandleJoinedToRoomProps {
  roomId: string | null;
  participantId: string;
  gameSessionId: string | null;
  region?: string | null;
}

interface HandleLeftRoomProps {
  participantId: string;
}

const { REACT_APP_FEATURE_SPATIAL_AUDIO } = env;

const enableSpatialAudio = REACT_APP_FEATURE_SPATIAL_AUDIO === 'true';

const {
  ConnectionInfo,
  JoinedToRoom,
  FullscreenSharing,
  LeftRoom,
  JoinedToEvent,
  LeftEvent,
  ExitEvent,
  JoinedToRegion,
  LeftRegion,
  ChangeRoomScale,
  BrowserUrlRequest,
  MuteParticipant,
  ShowParticipantInfo,
  SendToBriefcase,
  ObjectPoiClosed,
  ChangeParticipantProfile,
  ParticipantRoleUpdated,
  MessageToWebapp,
  Ue5WsConnected,
  Ue5WsDisconnected,
  FirstPersonViewRegion,
  FreePersonViewRegion,
  MuteConference,
  ParticipantPosition,
  SyncDataUe,
  AssistantIcon,
  StopDolbyScreenSharing,
  FollowPlayer,
  InviteFollowingByAdmin,
  StopFollowingByAdmin,
  CancelInviteFollowingByAdmin,
  AcceptFollowing,
  DisengageFollowing,
  StopFollowing,
  OpenTeleportModal,
  CloseTeleportModal,
  TeleportToRegion,
  ShowNotification,
  ShowDemoNotification,
  ShowWebNotification,
  TeleportFinished,
  OwnerTeleport,
  TerminateSession,
  AvatarRequested,
  AvatarReceived,
  AvatarSelect,
  MytaverseAction,
  GamecastSessionTerminated,
  PlayVideo,
  UEError,
  UEWarn,
  UEDebug,
  UEInfo,
  OpenOfficeHours,
  TakeScreenshotFinish,
  TakeScreenshotFailed,
  ExternalKeyboard,
  ShowHubMap,
  HideHubMap,
  MButtonClick,
  RaiseHandState,
  StopEntireSession,
  MovementCollisionIdentified,
  AssistantScriptUpdated,
  SpotFullscreenView,
} = WebsocketAction;

const { ShowControls, HideControls } = WebsocketMessage;

// Switch between behaviours for clicking a custom spot placeholder in editor mode.
// true  → show the choice dialog (image edit / assistant edit)
// false → skip the dialog and open the image upload editor directly (legacy behaviour)
const ENABLE_SPOT_CHOICE_DIALOG = false;

export const useGetWebsocketMsg = () => {
  const prevWebsocketMsgRef = useRef<LastWebsocketJsonMessageType | null>(null);

  //filter processed messages in case the context has changed
  return useCallback((websocketMsg: LastWebsocketJsonMessageType) => {
    if (prevWebsocketMsgRef.current === websocketMsg) {
      return null;
    }

    prevWebsocketMsgRef.current = websocketMsg;
    return websocketMsg;
  }, []);
};

// TODO should be split
export const useHandleWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const loadingScreenStartTimeRef = useRef<number | null>();

  const {
    setWebsocketConnectionInfo,
    sendJSONMessageToWebSocket,
    navigateToEventsPage,
    isDebugMode,
    sendMessageToUnreal,
  } = useMytaverse();
  const {
    setScreenshotDownloadUrl,
    fallbackScreenshotDownloadUrlRef,
    shouldConnectToConference,
    resetKeyboardPressedKeys,
    setAssistantScriptUpdateTick,
  } = useMytaverseEvent();
  const { handleDolbyWSMessages } = useConference();

  const handleVirtualAssistant = useVirtualAssistant();

  const {
    isJoiningConference,
    handleJoinToConference,
    setExpandedScreenSharing,
    setExpandedCameraSharing,
    leaveSession,
    conferenceName,
    changeParticipantsVolumeLevel,
  } = useCommunications();

  const {
    updateParticipant,
    recomputeUserEventPermissions,
    setParticipantState,
    pointsOfInterest,
    setPoiPreviewSrc,
    userFiles,
    setUserFiles,
    setShowControls,
    trackEventAnalytics,
    shareScreenPublishers,
    shareVideoPublishers,
    currentParticipant,
    setMuted,
    muted,
    setRoomDolbySpatialAudioScale,
    initMessageSended,
    currentRegion,
    currentRoom,
    setIsTeleporting,
    setShowExitButton,
    setIsHideWebUI,
    setIsHideWebUIExceptAssistant,
    setIsHideAllExceptTopBar,
    currentEvent,
    setIsUEModalActionInProgress,
    setPlayVideoUrl,
    setShowCustomizeSpotModal,
    setShowCustomSpotsEditor,
    setSelectedCustomizationTag,
    setSelectedCustomizationUeId,
    setSelectedCustomizationTargetSize,
    setSelectedCustomizationSpotType,
    setCustomizationSelectionRevision,
    setCustomisationItemSelectedTick,
    gameliftStreamsSessionKey,
    setExternalKeyboardConnected,
    setAiTriggerName,
    setObjectPoiId,
    setSpotFullscreenViewId,
    setSpotFullscreenViewTrigger,
    isBrandingModeEnabled,
  } = useMytaverseEvent();
  const {
    setPreviewingParticipant,
    setDisabledPersonView,
    setParticipantPosition,
    setScreenMediaStreams,
    screenMediaStreams,
    setUEWebcamScreenName,
    setIsFirstPersonView,
    setUEScreenScreenName,
  } = useConference() || {};

  const { showNotification, getSuccessNotification } = useNotification();
  const { t: translate } = useTranslation();
  const { handleUe5WsConnected, handleUe5WsDisconnected } =
    useHandleUEConnection();
  const { setOpen: setOpenEventScheduler } = useEventScheduler();
  const { setOpen: setOpenMapModal, toggleOpen } = useMapModal();

  const showLogoutCta = useMandatoryLogoutModal();

  useAsyncEffect(async () => {
    if (
      currentParticipant &&
      !currentParticipant.roomId &&
      !loadingScreenStartTimeRef.current
    ) {
      trackEventAnalytics(AnalyticsType.LoadingScreenStart, {});
      loadingScreenStartTimeRef.current = new Date().getTime();
    }
  }, [trackEventAnalytics, currentParticipant]);

  const handleJoinedToRoom = useCallback(
    ({ roomId, participantId, gameSessionId }: HandleJoinedToRoomProps) => {
      if (
        !roomId ||
        !participantId ||
        !initMessageSended ||
        (currentParticipant &&
          currentParticipant.participantId === participantId)
      ) {
        trackEventAnalytics(AnalyticsType.FinishUeInitialization);

        if (currentRegion) {
          sendJSONMessageToWebSocket({
            action: WebsocketAction.JoinedToRegion,
            timestamp: Date.now(),
            participantId: participantId,
            region: currentRegion.region,
            roomId,
          });
        }
        return;
      }

      setTimeout(() => {
        MytaverseLogger.log(
          `Participant ${participantId} joined to room ${roomId}`,
        );
        setParticipantState(participantId, {
          roomId,
          region: currentRegion || null,
          regions: [],
          gameSessionId,
        });
      }, 7000);
    },
    [
      setParticipantState,
      currentParticipant,
      initMessageSended,
      currentRegion,
    ],
  );

  const handleLeftRoom = useCallback(
    ({ participantId }: HandleLeftRoomProps) => {
      if (!participantId) {
        return;
      }

      MytaverseLogger.log(`Participant ${participantId} left to room`);

      setParticipantState(participantId, {
        roomId: null,
        region: null,
        regions: [],
        gameSessionId: null,
      });
    },
    [setParticipantState],
  );

  const handleTeleportFinished = useCallback(() => {
    setTimeout(() => {
      if (loadingScreenStartTimeRef.current) {
        trackEventAnalytics(AnalyticsType.LoadingScreenFinish, {
          duration: new Date().getTime() - loadingScreenStartTimeRef.current,
        });
        loadingScreenStartTimeRef.current = null;
      }
      //e5WebsocketConnected with timeout
      setIsTeleporting(false);
    }, 3000);
  }, [setIsTeleporting]);

  const handleOwnerTeleport = useCallback(
    (ownerId: string, followerIds: string[]) => {
      if (!currentParticipant || ownerId === currentParticipant.participantId) {
        return;
      }
      if (followerIds.indexOf(currentParticipant.participantId) === -1) {
        return;
      }

      MytaverseLogger.log(`Teleport ownerId: ${ownerId}`);
      setIsTeleporting(true);
    },
    [currentParticipant, setIsTeleporting],
  );

  const handleStopDolbySharing = useCallback(
    (mediaStreamId: string) => {
      if (setScreenMediaStreams) {
        setScreenMediaStreams((prev) =>
          prev.filter((s) => s.stream.id !== mediaStreamId),
        );
      }
    },
    [setScreenMediaStreams, screenMediaStreams],
  );

  const handleShowParticipantInfo = useCallback(
    async (participantId = '') => {
      if (!participantId) {
        return;
      }

      const participantProfile = await EventsService.getParticipantProfile(
        participantId,
      );
      if (participantProfile && setPreviewingParticipant) {
        setPreviewingParticipant(participantProfile);
      }
    },
    [setPreviewingParticipant],
  );

  const handleSendToBriefcase = useCallback(
    async (
      { fileName, downloadUrl = '', mediaType }: SendToBriefcasePropsTypeFull,
      currentRoom?: IRoom | null,
    ) => {
      if (!fileName || !pointsOfInterest) {
        return;
      }

      setPoiPreviewSrc({
        downloadUrl,
        fileName,
        mediaType,
      });

      const existingFile = userFiles.some(
        ({ displayName }) => displayName === fileName,
      );

      if (existingFile) {
        return;
      }

      const file = pointsOfInterest.find(
        ({ displayName }) => displayName === fileName,
      );

      if (file) {
        setUserFiles((prev) => Array.from(new Set([file, ...prev])));
      } else if (currentRoom) {
        try {
          console.log('Retrieving latest POIs');
          const pois = await EventsService.getPointOfInterest(
            currentRoom.roomId,
          );

          const filePoi = pois.find(({ name }) => name === fileName);

          if (filePoi) {
            setUserFiles((prev) => Array.from(new Set([filePoi, ...prev])));
          }
        } catch (e) {
          MytaverseLogger.error(
            `Failed to retrieve latest POIs for roomId=${
              currentRoom.roomId
            }, fileName=${fileName}: ${(e as Error)?.message || String(e)}`,
          );
        }
      }
    },
    [pointsOfInterest, setPoiPreviewSrc, setUserFiles, userFiles],
  );

  const handleMytaverseAction = useCallback(async (tag: string) => {
    if (tag === 'MytaEvent.Action.CelebratoryMoment.Download') {
      try {
        setIsUEModalActionInProgress(true);
        const frontScreenUrl = await downloadGamecastScreenshot(
          'Congratulations & Welcome',
          false,
        );

        if (frontScreenUrl) {
          fallbackScreenshotDownloadUrlRef.current = frontScreenUrl;
        }

        const screenshotUrls = await ParticipantsService.generateScreenshotUrls(
          'Congratulations & Welcome.png',
        );

        sendMessageToUnreal({
          action: WebsocketAction.TakeScreenshotRequest,
          name: 'Congratulations & Welcome',
          uploadUrl: screenshotUrls.uploadUrl,
        });

        setScreenshotDownloadUrl(screenshotUrls.downloadUrl);
      } catch (error) {
        showNotification(
          getErrorNotification({
            message: translate('notifications.download.screenshotError'),
          }),
        );
      } finally {
        setIsUEModalActionInProgress(false);
      }
    }
  }, []);

  const trackPoiClick = useCallback(
    (fileName: string) => {
      if (!fileName || !pointsOfInterest) {
        return;
      }

      const poi = pointsOfInterest.find(
        ({ displayName }) => displayName === fileName,
      );

      if (poi) {
        const { id, asset, displayImage, displayName, mediaType, room } = poi;

        trackEventAnalytics(AnalyticsType.PoiClick, {
          poi: { id, asset, displayImage, displayName, mediaType, room },
        });
      }
    },
    [pointsOfInterest, trackEventAnalytics],
  );

  const handleParticipantPositionChanged = useCallback(
    async ({
      timestamp,
      gameSessionId,
      eventId,
      roomId,
      x,
      y,
      z,
      r,
      regionName,
      regionSpatialType,
      isHideWebUI,
      isHideWebUIExceptAssistant,
      isHideAllExceptTopBar,
      nearestParticipants,
      isSeatRegion,
    }: IParticipantPositionMessage) => {
      if (!currentParticipant || !currentEvent) {
        console.log({ currentParticipant, currentEvent });
        return;
      }

      const currentRegionName = currentParticipant.region
        ? currentParticipant.region.region
        : '';

      if (
        !isHideWebUI &&
        !isHideWebUIExceptAssistant &&
        !isHideAllExceptTopBar &&
        shouldConnectToConference
      ) {
        if (!isJoiningConference && currentParticipant.roomId) {
          await handleJoinToConference(
            currentEvent.eventId,
            currentParticipant.roomId,
            regionName,
          );
        }
      }

      if (
        enableSpatialAudio &&
        nearestParticipants &&
        regionSpatialType !== SpatialType.OneToMany &&
        regionSpatialType !== SpatialType.None
      ) {
        try {
          const { isEdgeOnIOSOrAndroid } = await import('../GameCast/helpers');
          const effectiveSpatialType = isEdgeOnIOSOrAndroid()
            ? SpatialType.DistanceAudio
            : regionSpatialType;
          changeParticipantsVolumeLevel(
            nearestParticipants,
            effectiveSpatialType,
          );
        } catch {
          changeParticipantsVolumeLevel(nearestParticipants, regionSpatialType);
        }
      }

      const regionContextChanged =
        !currentParticipant.roomId ||
        currentParticipant.roomId !== roomId ||
        currentParticipant.gameSessionId !== gameSessionId ||
        currentRegionName !== regionName;

      const nextIsSeatRegion = !!isSeatRegion;
      const seatRegionChanged =
        !!currentParticipant.isSeatRegion !== nextIsSeatRegion;

      if (regionContextChanged) {
        setParticipantState(currentParticipant.participantId, {
          eventId,
          roomId,
          region: regionName
            ? {
                timestamp: Date.now(),
                region: regionName,
                state: 'joined',
                regionSpatialType:
                  regionSpatialType && regionSpatialType.length !== 0
                    ? regionSpatialType
                    : SpatialType.SpatialAudio,
                isSeatRegion: nextIsSeatRegion,
              }
            : null,
          regions: [],
          gameSessionId,
          isSeatRegion: nextIsSeatRegion,
        });
      } else if (seatRegionChanged) {
        setParticipantState(currentParticipant.participantId, {
          isSeatRegion: nextIsSeatRegion,
          ...(currentParticipant.region
            ? {
                region: {
                  ...currentParticipant.region,
                  isSeatRegion: nextIsSeatRegion,
                },
              }
            : {}),
        });
      }

      if (setParticipantPosition) {
        setParticipantPosition({
          timestamp,
          x: Math.trunc(x),
          y: Math.trunc(y),
          z: Math.trunc(z),
          r: Math.trunc(r),
        });
      }
      setSessionStorageValue(SessionStorage.ParticipantPosition, {
        x,
        y,
        z,
        r,
      });
    },
    [
      currentParticipant,
      setParticipantPosition,
      setParticipantState,
      isJoiningConference,
      handleJoinToConference,
      currentEvent,
      shouldConnectToConference,
    ],
  );

  const handleChangeParticipantProfile = useCallback(
    async (participantId: string) => {
      if (!participantId) {
        return;
      }

      const updatedParticipant = await EventsService.getParticipant(
        participantId,
      );

      if (updatedParticipant) {
        updateParticipant(participantId, updatedParticipant);
      }
    },
    [updateParticipant],
  );

  // Apply an experience role change in realtime (MV-1303). The role/isFacilitator
  // update keeps every viewer's participant list and facilitator badges in sync;
  // for the affected user we also re-resolve their permissions and event role.
  const handleParticipantRoleUpdated = useCallback(
    ({
      participantId,
      role,
      isFacilitator,
    }: {
      participantId: string;
      role?: string;
      isFacilitator?: boolean;
    }) => {
      if (!participantId) {
        return;
      }

      updateParticipant(participantId, { eventRole: role, isFacilitator });

      if (
        role &&
        currentParticipant &&
        currentParticipant.participantId === participantId
      ) {
        recomputeUserEventPermissions(role);
      }
    },
    [updateParticipant, recomputeUserEventPermissions, currentParticipant],
  );

  const handleMessageToWebapp = useCallback(
    (message: WebsocketMessage | undefined) => {
      if (message === HideControls) {
        setShowControls(false);
        return;
      }

      if (message === ShowControls) {
        setTimeout(() => {
          setShowControls(true);
        }, 8000);
        return;
      }
    },
    [setShowControls],
  );

  const handleMuteConference = useCallback(
    ({ conferenceId, mutedBy }: { conferenceId: string; mutedBy: string }) => {
      if (
        conferenceId === conferenceName &&
        currentParticipant &&
        mutedBy !== currentParticipant.participantId &&
        !muted
      ) {
        setMuted(true);
        showNotification(
          getSuccessNotification({
            title: translate(
              `notifications.mutedByModerator.${
                !muted ? 'title' : 'unmutedTitle'
              }`,
            ),
            message: translate(
              `notifications.mutedByModerator.${!muted ? 'muted' : 'unmuted'}`,
            ),
          }),
        );
      }
    },
    [currentParticipant, muted, setMuted, showNotification, conferenceName],
  );

  const handleFirstPersonViewRegion = useCallback(() => {
    if (setDisabledPersonView) {
      setDisabledPersonView(true);
    }
  }, [setDisabledPersonView]);

  const handleFreePersonViewRegion = useCallback(() => {
    if (setDisabledPersonView) {
      setDisabledPersonView(false);
    }
  }, [setDisabledPersonView]);

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const {
      action,
      timestamp,
      gameSessionId = '',
      eventId = '',
      roomId = '',
      x,
      y,
      z,
      r,
      data,
      message,
      participantId = '',
      fileName = '',
      downloadUrl = '',
      regionSpatialType = SpatialType.SpatialAudio,
      mediaType = '',
      conferenceId,
      mutedBy,
      mediaStreamId,
      playerPosition,
      dolbySpatialAudioScale,
      tag = '',
      url,
      sessionKey,
      attached,
      poiId = '',
    } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    let { region = '', regionName = '' } = lastJsonMessage;

    if (region) {
      region = camelCase(region);
    }

    if (regionName) {
      regionName = camelCase(regionName);
    }

    switch (action) {
      case WebsocketAction.CustomisationItemSelected: {
        const ueId = (lastJsonMessage as any)?.id || null;
        if (!ueId) {
          showNotification(
            getNotification({
              type: NOTIFICATION_TYPES.ERROR,
              title: translate('notifications.error'),
              message: 'Missing UE identifier for customization item.',
            }),
          );
          break;
        }
        const rawWidth = (lastJsonMessage as any)?.width;
        const rawHeight = (lastJsonMessage as any)?.height;
        const width =
          typeof rawWidth === 'number'
            ? rawWidth
            : typeof rawWidth === 'string'
            ? parseInt(rawWidth, 10)
            : NaN;
        const height =
          typeof rawHeight === 'number'
            ? rawHeight
            : typeof rawHeight === 'string'
            ? parseInt(rawHeight, 10)
            : NaN;

        if (
          Number.isFinite(width) &&
          Number.isFinite(height) &&
          width > 0 &&
          height > 0
        ) {
          setSelectedCustomizationTargetSize({ width, height });
        } else {
          setSelectedCustomizationTargetSize(null);
        }
        const tag = (lastJsonMessage as any)?.id || null;
        const rawSpotType = (lastJsonMessage as any)?.type;
        let spotType: 'IMAGE' | 'VIDEO' | 'VIDEO_STATIC' | 'QR' = 'IMAGE';
        if (typeof rawSpotType === 'string') {
          const normalized = rawSpotType.toUpperCase();
          if (normalized === 'VIDEO') {
            spotType = 'VIDEO';
          } else if (normalized === 'VIDEO_STATIC') {
            spotType = 'VIDEO_STATIC';
          } else if (normalized === 'QR') {
            spotType = 'QR';
          }
        }

        setSelectedCustomizationSpotType(spotType);
        setSelectedCustomizationUeId(ueId);
        setSelectedCustomizationTag(tag);
        if (isBrandingModeEnabled) {
          setCustomizationSelectionRevision((n) => n + 1);
        } else if (ENABLE_SPOT_CHOICE_DIALOG) {
          setShowCustomizeSpotModal(true);
        } else {
          setShowCustomSpotsEditor(true);
        }

        setCustomisationItemSelectedTick((t) => t + 1);
        break;
      }
      case SpotFullscreenView: {
        const brandingSpotId = (lastJsonMessage as any)?.brandingSpotId ?? null;
        // UE now reports the AI trigger (region) that owns the clicked spot.
        // Without it the assistant can't tell a spot in Expo #2 (clicked while
        // standing in Expo #1) from the avatar's live region.
        const spotAiTrigger = (lastJsonMessage as any)?.aiTrigger ?? null;
        MytaverseLogger.log('SpotFullscreenView WS received', {
          brandingSpotId,
          spotAiTrigger,
        });
        // Set the owning trigger BEFORE the spot id so the bridge effect (keyed
        // on spotFullscreenViewId) reads a populated trigger on the same tick.
        setSpotFullscreenViewTrigger(spotAiTrigger);
        setSpotFullscreenViewId(brandingSpotId);
        break;
      }
      case ConnectionInfo: {
        setWebsocketConnectionInfo(data);
        break;
      }

      case JoinedToRoom: {
        handleJoinedToRoom({ roomId, participantId, region, gameSessionId });
        break;
      }

      case PlayVideo: {
        setPlayVideoUrl(url);
        break;
      }

      case FullscreenSharing: {
        const { type } = lastJsonMessage;
        if (!type) {
          return;
        }
        if (type === EFullscreenType.Screen) {
          setExpandedCameraSharing(false);
          setExpandedScreenSharing(true);
        }
        if (type === EFullscreenType.Camera) {
          setExpandedScreenSharing(false);
          setExpandedCameraSharing(true);
        }

        break;
      }

      case OpenOfficeHours: {
        if (env.REACT_APP_FEATURE_EVENT_SCHEDULER === 'true') {
          setOpenEventScheduler(true);
        }
        break;
      }

      case ShowHubMap: {
        setOpenMapModal(true);
        break;
      }

      case HideHubMap: {
        setOpenMapModal(false);
        break;
      }

      case MButtonClick: {
        toggleOpen();
        break;
      }

      case LeftRoom: {
        handleLeftRoom({
          participantId,
        });
        break;
      }

      case TeleportFinished: {
        handleTeleportFinished();
        break;
      }
      case OwnerTeleport: {
        const { ownerId, followerIds } = lastJsonMessage;
        if (ownerId && followerIds?.length) {
          handleOwnerTeleport(ownerId, followerIds);
        }
        break;
      }

      case AssistantIcon: {
        if (lastJsonMessage.payload) {
          handleVirtualAssistant(lastJsonMessage.payload);
        }

        break;
      }

      case AssistantScriptUpdated: {
        setAssistantScriptUpdateTick((prev) => prev + 1);
        break;
      }

      case ParticipantPosition:
      case SyncDataUe: {
        const messageTimestamp = timestamp ? parseInt(timestamp) : 0;

        const {
          showExitButton = false,
          isHideWebUI = false,
          isHideWebUIExceptAssistant = false,
          isHideAllExceptTopBar = false,
          playerView,
          nearestParticipants,
        } = lastJsonMessage;

        const audioTrigger = (lastJsonMessage as any)?.audioTrigger;
        const audioTriggerVideoWebcamScreenId =
          audioTrigger?.videoWebcamScreenId;
        const audioTriggerVideoScreenScreenId =
          audioTrigger?.videoScreenScreenId;
        const rootVideoWebcamScreenId = (lastJsonMessage as any)
          ?.videoWebcamScreenId as string | undefined;
        const rootVideoScreenScreenId = (lastJsonMessage as any)
          ?.videoScreenScreenId as string | undefined;
        const derivedIsSeatRegion = audioTrigger?.isSeatRegion ?? false;
        const rootCurrentGameSessionId = (lastJsonMessage as any)
          ?.CurrentGameSessionId as string | undefined;

        const normalizedGameSessionId =
          gameSessionId ||
          rootCurrentGameSessionId ||
          playerPosition?.CurrentGameSessionId ||
          '';
        const normalizedEventId = eventId || playerPosition?.eventId || '';
        const normalizedRoomId = roomId || playerPosition?.roomId || '';
        const normalizedX = x ?? playerPosition?.x;
        const normalizedY = y ?? playerPosition?.y;
        const normalizedZ = z ?? playerPosition?.z;
        const normalizedR = r ?? playerPosition?.r;

        const audioTriggerRegionId = audioTrigger?.audioTriggerId
          ? camelCase(String(audioTrigger.audioTriggerId))
          : '';
        const effectiveRegionName = regionName || audioTriggerRegionId;
        const effectiveAiTriggerName = audioTrigger?.aiTriggerId || '';
        setAiTriggerName(effectiveAiTriggerName);

        const nextWebcamScreenName =
          audioTriggerVideoWebcamScreenId ?? rootVideoWebcamScreenId ?? null;
        const nextScreenScreenName =
          audioTriggerVideoScreenScreenId ?? rootVideoScreenScreenId ?? null;

        handleParticipantPositionChanged({
          timestamp: messageTimestamp,
          gameSessionId: normalizedGameSessionId,
          eventId: normalizedEventId,
          roomId: normalizedRoomId,
          regionName: effectiveRegionName,
          regionSpatialType,
          x: (normalizedX ?? 0) as number,
          y: (normalizedY ?? 0) as number,
          z: (normalizedZ ?? 0) as number,
          r: (normalizedR ?? 0) as number,
          isHideWebUI,
          isHideWebUIExceptAssistant,
          isHideAllExceptTopBar,
          nearestParticipants,
          isSeatRegion: !!derivedIsSeatRegion,
        });

        if (setUEWebcamScreenName) {
          setUEWebcamScreenName(nextWebcamScreenName || null);
        }
        if (setUEScreenScreenName) {
          setUEScreenScreenName(nextScreenScreenName || null);
        }

        if (
          isHideWebUI ||
          isHideWebUIExceptAssistant ||
          isHideAllExceptTopBar
        ) {
          leaveSession();
        }

        const updateState = (
          setState: React.Dispatch<React.SetStateAction<IWebsocketMessageFlag>>,
          messageTimestamp: number,
          newFlag: boolean,
        ) => {
          setState((prevState) => {
            if (
              prevState.timestamp > messageTimestamp ||
              prevState.flag === newFlag
            ) {
              return prevState;
            }

            return { flag: newFlag, timestamp: messageTimestamp };
          });
        };

        updateState(setShowExitButton, messageTimestamp, showExitButton);
        updateState(setIsHideWebUI, messageTimestamp, isHideWebUI);
        updateState(
          setIsHideWebUIExceptAssistant,
          messageTimestamp,
          isHideWebUIExceptAssistant,
        );
        updateState(
          setIsHideAllExceptTopBar,
          messageTimestamp,
          isHideAllExceptTopBar,
        );

        if (playerView && setIsFirstPersonView) {
          updateState(
            setIsFirstPersonView,
            messageTimestamp,
            playerView === PlayerViewUEEnum.FirstPerson,
          );
        }

        break;
      }

      case ChangeRoomScale: {
        if (roomId && dolbySpatialAudioScale) {
          setRoomDolbySpatialAudioScale(roomId, dolbySpatialAudioScale);
        }

        break;
      }
      case ShowParticipantInfo: {
        handleShowParticipantInfo(participantId);
        break;
      }
      case ObjectPoiClosed: {
        setObjectPoiId(null);
        break;
      }
      case SendToBriefcase: {
        if (!mediaType) {
          return;
        }

        if (
          (mediaType as string).toUpperCase() === 'AI_SCRIPT' ||
          (mediaType as string).toUpperCase() === 'OBJECT_3D'
        ) {
          if (poiId) {
            setObjectPoiId(poiId);
            setTimeout(() => setObjectPoiId(''), 0);
          }
          break;
        }

        handleSendToBriefcase(
          {
            fileName,
            downloadUrl,
            mediaType:
              // @ts-ignore
              mediaType === 'websiteUrl'
                ? PoiMediaType.WEB_BROWSER
                : (mediaType.toUpperCase() as PoiMediaType),
          },
          currentRoom,
        );
        trackPoiClick(fileName);

        break;
      }
      case ChangeParticipantProfile: {
        handleChangeParticipantProfile(participantId);
        break;
      }
      case ParticipantRoleUpdated: {
        handleParticipantRoleUpdated({
          participantId,
          role: (lastJsonMessage as any)?.role,
          isFacilitator: (lastJsonMessage as any)?.isFacilitator,
        });
        break;
      }
      case MessageToWebapp: {
        handleMessageToWebapp(message);
        break;
      }

      case Ue5WsConnected: {
        handleUe5WsConnected();
        break;
      }
      case Ue5WsDisconnected: {
        handleUe5WsDisconnected();
        break;
      }

      case FirstPersonViewRegion: {
        handleFirstPersonViewRegion();

        break;
      }

      case FreePersonViewRegion: {
        handleFreePersonViewRegion();
        break;
      }

      case MuteConference: {
        if (!conferenceId || !mutedBy) {
          return;
        }

        handleMuteConference({
          conferenceId,
          mutedBy,
        });
        break;
      }

      case StopDolbyScreenSharing: {
        handleStopDolbySharing(mediaStreamId as string);
        break;
      }

      case MytaverseAction: {
        handleMytaverseAction(tag);
        break;
      }

      case GamecastSessionTerminated: {
        navigateToEventsPage({
          message: 'notifications.duplicateGamecastSession',
        });
        break;
      }

      case ExternalKeyboard:
        if (attached !== undefined) {
          setExternalKeyboardConnected(attached);
        }
        break;

      case MovementCollisionIdentified:
        MytaverseLogger.log('Movement collision identified. Reset pressed keys');
        resetKeyboardPressedKeys();
        break;

      case StopEntireSession: {
        showLogoutCta();
        break;
      }

      default:
        break;
    }
  }, [
    lastJsonMessage,
    shareScreenPublishers,
    handleChangeParticipantProfile,
    handleParticipantRoleUpdated,
    handleJoinedToRoom,
    handleMessageToWebapp,
    handleSendToBriefcase,
    handleShowParticipantInfo,
    setUEScreenScreenName,
    setUEWebcamScreenName,
    handleUe5WsConnected,
    handleUe5WsDisconnected,
    handleFirstPersonViewRegion,
    handleFreePersonViewRegion,
    handleMytaverseAction,
    setRoomDolbySpatialAudioScale,
    setWebsocketConnectionInfo,
    trackPoiClick,
    setParticipantState,
    handleMuteConference,
    shareVideoPublishers,
    navigateToEventsPage,
    handleParticipantPositionChanged,
    gameliftStreamsSessionKey,
    handleVirtualAssistant,
    isDebugMode,
    showLogoutCta,
    resetKeyboardPressedKeys
  ]);

  // Dolby WS handlers
  useEffect(() => {
    if (
      !lastJsonMessage ||
      !currentEvent ||
      currentEvent.eventId === '6f941d5c-b6db-4f4b-b79b-8b4756f305bc' ||
      !handleDolbyWSMessages
    ) {
      return;
    }

    handleDolbyWSMessages(lastJsonMessage);
  }, [lastJsonMessage, handleDolbyWSMessages, currentEvent]);
};

export const useHandleEventWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { setParticipantState, gameliftStreamsSessionKey } =
    useMytaverseEvent();
  const { isDebugMode, navigateToEventsPage } = useMytaverse();
  const { resetKeyboardPressedKeys } = useMytaverseEvent();

  const handleJoinedToEvent = useCallback(
    async ({ participantId, eventId }: IWebSocketConnectionInfo) => {
      //stopCommunicationFromUeTimer();
      setParticipantState(participantId, {
        eventId,
        roomId: null,
        region: null,
      });
    },
    [setParticipantState],
  );

  const handleLeftToEvent = useCallback(
    (participantId = '') => {
      if (!participantId) {
        return;
      }

      setParticipantState(participantId, {
        eventId: null,
        roomId: null,
        region: null,
      });
    },
    [setParticipantState],
  );

  const handleExitEvent = useCallback(() => {
    navigateToEventsPage({
      message: 'notifications.experienceCanceled',
      type: NOTIFICATION_TYPES.WARNING,
    });
  }, [navigateToEventsPage]);

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, data, participantId = '', sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    switch (action) {
      case JoinedToEvent: {
        handleJoinedToEvent(data);
        break;
      }
      case LeftEvent: {
        handleLeftToEvent(participantId);
        break;
      }
      case ExitEvent: {
        handleExitEvent();
        break;
      }
      default:
        break;
    }
  }, [
    lastJsonMessage,
    handleJoinedToEvent,
    handleLeftToEvent,
    handleExitEvent,
    gameliftStreamsSessionKey,
    isDebugMode,
    resetKeyboardPressedKeys
  ]);
};

export const useHandleScreenshotWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { gameliftStreamsSessionKey } = useMytaverseEvent();
  const { isDebugMode } = useMytaverse();
  const {
    screenshotDownloadUrl,
    setScreenshotDownloadUrl,
    fallbackScreenshotDownloadUrlRef,
  } = useMytaverseEvent();
  const { showNotification } = useNotification();
  const { t: translate } = useTranslation();

  const timeoutId = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!screenshotDownloadUrl || !fallbackScreenshotDownloadUrlRef.current) {
      return;
    }

    timeoutId.current = setTimeout(() => {
      if (fallbackScreenshotDownloadUrlRef.current) {
        restoringFocusDownload(
          fallbackScreenshotDownloadUrlRef.current,
          'Congratulations & Welcome.png',
        );

        fallbackScreenshotDownloadUrlRef.current = null;
        setScreenshotDownloadUrl(null);
      }

      timeoutId.current = null;
    }, 5000);

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
    };
  }, [screenshotDownloadUrl]);

  useAsyncEffect(async () => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, name, sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    switch (action) {
      case TakeScreenshotFinish: {
        if (screenshotDownloadUrl) {
          if (timeoutId.current) {
            clearTimeout(timeoutId.current);
            timeoutId.current = null;
          }

          const fileBody = await FilesService.downloadFileFromS3(
            screenshotDownloadUrl,
          );

          const blob = new Blob([fileBody], { type: 'image/png' });
          const imageUrl = URL.createObjectURL(blob);

          restoringFocusDownload(imageUrl, name || 'screenshot.png');

          setScreenshotDownloadUrl(null);
          if (fallbackScreenshotDownloadUrlRef.current) {
            URL.revokeObjectURL(fallbackScreenshotDownloadUrlRef.current);
            fallbackScreenshotDownloadUrlRef.current = null;
          }
        }

        break;
      }
      case TakeScreenshotFailed: {
        showNotification(
          getErrorNotification({
            message: translate('notifications.download.screenshotError'),
          }),
        );
        break;
      }
      default:
        break;
    }
  }, [
    lastJsonMessage,
    gameliftStreamsSessionKey,
    isDebugMode,
    screenshotDownloadUrl,
  ]);
};

export const useHandleMuteParticipantWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const [showMutedByAdminNotification, setShowMutedByAdminNotification] =
    useState(false);

  const { setMuted, gameliftStreamsSessionKey } = useMytaverseEvent();
  const { showNotification, getSuccessNotification } = useNotification();
  const { t: translate } = useTranslation();
  const { isDebugMode } = useMytaverse();

  const handleMuteParticipant = useCallback(
    (muted: boolean) => {
      if (showMutedByAdminNotification) {
        return;
      }

      setMuted(muted);
      setShowMutedByAdminNotification(true);
      showNotification(
        getSuccessNotification({
          title: translate('notifications.mutedByModerator.title'),
          message: translate(
            `notifications.mutedByModerator.${muted ? 'muted' : 'unmuted'}`,
          ),
          onClose: () => setShowMutedByAdminNotification(false),
        }),
      );
    },
    [
      getSuccessNotification,
      setMuted,
      showMutedByAdminNotification,
      showNotification,
      translate,
    ],
  );

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, data, sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    if (action === MuteParticipant) {
      handleMuteParticipant(data?.muted || true);
    }
  }, [
    handleMuteParticipant,
    lastJsonMessage,
    gameliftStreamsSessionKey,
    isDebugMode,
  ]);
};

export const useHandleRaiseHandWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const {
    setParticipantState,
    gameliftStreamsSessionKey,
    currentParticipantId,
    setIsHandRaised,
  } = useMytaverseEvent();
  const { isDebugMode } = useMytaverse();

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, participantId, handRaised, handRaisedAt, sessionKey } =
      lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    if (action === RaiseHandState && participantId) {
      setParticipantState(participantId, {
        handRaised: handRaised || false,
        handRaisedAt: handRaisedAt,
      });

      if (participantId === currentParticipantId) {
        setIsHandRaised(handRaised || false);
      }
    }
  }, [
    lastJsonMessage,
    setParticipantState,
    gameliftStreamsSessionKey,
    isDebugMode,
    currentParticipantId,
    setIsHandRaised,
  ]);
};

export const useHandleBrowserUrlRequestWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { gameliftStreamsSessionKey } = useMytaverseEvent();
  const { isDebugMode } = useMytaverse();

  const [openBrowserUrlDialog, setOpenBrowserUrlDialog] = useState(false);

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    if (lastJsonMessage?.action === BrowserUrlRequest) {
      setOpenBrowserUrlDialog(true);
    }
  }, [lastJsonMessage, gameliftStreamsSessionKey]);

  return {
    openBrowserUrlDialog,
    setOpenBrowserUrlDialog,
    isDebugMode,
  };
};

export const useHandleJoinedToRegionWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const {
    currentParticipantId,
    setParticipantState,
    gameliftStreamsSessionKey,
  } = useMytaverseEvent();

  const { isDebugMode } = useMytaverse();

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const {
      action,
      timestamp,
      region,
      participantId,
      sessionKey,
      isSeatRegion = false,
    } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    if (!region || !participantId || currentParticipantId === participantId) {
      return;
    }

    switch (action) {
      case JoinedToRegion:
        setParticipantState(participantId, {
          region: {
            timestamp: timestamp ? parseInt(timestamp, 10) : Date.now(),
            region,
            state: 'joined',
            isSeatRegion,
          },
        });
        break;

      case LeftRegion:
        setParticipantState(participantId, {
          region: null,
        });
        break;

      default:
        break;
    }
  }, [
    lastJsonMessage,
    currentParticipantId,
    setParticipantState,
    gameliftStreamsSessionKey,
    isDebugMode,
  ]);
};

export const useHandleFollowMeWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const {
    currentEvent,
    currentParticipant,
    setPendingFollowersData,
    setAcceptedFollowersData,
    gameliftStreamsSessionKey,
  } = useMytaverseEvent();
  const { sendMessageToEvent, isDebugMode } = useMytaverse();
  const [play] = useSound(FollowMeSound, {
    volume: 0.15,
  });

  useEffect(() => {
    if (!lastJsonMessage || !currentEvent?.eventId) {
      return;
    }

    const {
      action,
      followerId = '',
      ownerId = '',
      isEventMessage = false,
      message = '',
      sessionKey,
    } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    const followerData: IFollowerData = {
      userId: followerId,
      adminId: ownerId,
    };

    const redirectedActions = [
      AcceptFollowing,
      DisengageFollowing,
      StopFollowing,
      // InviteFollowingByAdmin,
    ];

    const redirectMessageToEvent = (
      message: UnknownObjectType<string, string>,
    ) => {
      sendMessageToEvent(currentEvent?.eventId || '', {
        ...message,
        isEventMessage: true,
      });
    };

    const handleMessage = async () => {
      if (
        redirectedActions.includes(action as WebsocketAction) &&
        !isEventMessage
      ) {
        redirectMessageToEvent({
          action,
          followerId,
          ownerId,
        });
        return;
      }

      switch (action) {
        case FollowPlayer: {
          if (followerData.userId === currentParticipant?.participantId) {
            play();
          }

          // all api updates will be done from adminId
          if (followerData.adminId === currentParticipant?.participantId) {
            await EventsService.sendFollowingInvitation({
              eventId: currentEvent?.eventId,
              participantId: followerData.userId,
              groupLeadId: followerData.adminId,
            });
            await EventsService.acceptFollowingInvitation({
              eventId: currentEvent?.eventId,
              participantId: followerData.userId,
            });
          }

          setAcceptedFollowersData((prev) => prev.concat(followerData));
          break;
        }
        case InviteFollowingByAdmin: {
          if (followerData.userId === currentParticipant?.participantId) {
            play();
          }

          // all api updates will be done from adminId
          if (followerData.adminId === currentParticipant?.participantId) {
            await EventsService.sendFollowingInvitation({
              eventId: currentEvent?.eventId,
              participantId: followerData.userId,
              groupLeadId: followerData.adminId,
            });
          }

          setPendingFollowersData((prev) => prev.concat(followerData));
          break;
        }
        case StopFollowingByAdmin:
        case StopFollowing: {
          // all api updates will be done from adminId
          if (followerData.adminId === currentParticipant?.participantId) {
            await EventsService.declineFollowingInvitation({
              eventId: currentEvent?.eventId,
              participantId: followerData.userId,
            });
          }

          setAcceptedFollowersData((prev) =>
            prev.filter(
              (data) =>
                !(
                  data.userId === followerData.userId &&
                  data.adminId === followerData.adminId
                ),
            ),
          );
          break;
        }
        case CancelInviteFollowingByAdmin:
        case DisengageFollowing: {
          // all api updates will be done from adminId
          if (followerData.adminId === currentParticipant?.participantId) {
            await EventsService.declineFollowingInvitation({
              eventId: currentEvent?.eventId,
              participantId: followerData.userId,
            });
          }

          setPendingFollowersData((prev) =>
            prev.filter(
              (data) =>
                data.userId !== followerData.userId &&
                data.adminId !== followerData.adminId,
            ),
          );
          break;
        }
        case AcceptFollowing: {
          // all api updates will be done from adminId
          if (followerData.adminId === currentParticipant?.participantId) {
            await EventsService.acceptFollowingInvitation({
              eventId: currentEvent?.eventId,
              participantId: followerData.userId,
            });
          }

          setPendingFollowersData((prev) =>
            prev.filter(
              (data) =>
                data.userId !== followerData.userId &&
                data.adminId !== followerData.adminId,
            ),
          );
          setAcceptedFollowersData((prev) => prev.concat(followerData));
          break;
        }
        case ShowNotification: {
          showNotification(
            getErrorNotification({
              message: message,
              withReloadButton: true,
              closeManually: true,
            }),
          );
          break;
        }
        case ShowDemoNotification: {
          showNotification({
            message,
            type: NOTIFICATION_TYPES.WARNING,
            closeManually: true,
            withDismissButton: false,
          });
          break;
        }
        case ShowWebNotification: {
          const { message = '', type = '' } = lastJsonMessage.payload || {};

          if (message) {
            const messageType = type
              ? NOTIFICATION_TYPES[type as keyof typeof NOTIFICATION_TYPES]
              : undefined;
            showNotification({
              message,
              type: messageType,
              closeManually: false,
              withDismissButton: false,
            });
          }

          break;
        }
        case TerminateSession: {
          navigate(ROUTES.LOGIN);
          break;
        }
      }
    };

    handleMessage();
  }, [
    lastJsonMessage,
    currentParticipant?.participantId,
    currentEvent?.eventId,
    setPendingFollowersData,
    play,
    setAcceptedFollowersData,
    sendMessageToEvent,
    gameliftStreamsSessionKey,
    isDebugMode,
  ]);
};

export const useHandleTeleportingModalWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { trackEventAnalytics, gameliftStreamsSessionKey } =
    useMytaverseEvent();
  const { openTeleport, closeTeleport } = useTeleportOpen();
  const { isTeleportFeatureEnabled } = useClientSpecificTeleportVisibility();

  const { isDebugMode } = useMytaverse();

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, region, sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    const handleMessage = async () => {
      switch (action) {
        case OpenTeleportModal:
          if (!isTeleportFeatureEnabled) {
            break;
          }
          MytaverseLogger.log('Open teleport modal (from UE)');
          openTeleport({ fromEngine: true });
          break;
        case CloseTeleportModal:
          MytaverseLogger.log('Close teleport modal (from UE)');
          closeTeleport();
          break;
        case TeleportToRegion:
          trackEventAnalytics(AnalyticsType.TeleportToRegion, { region });
          break;
        default:
          break;
      }
    };

    handleMessage();
  }, [
    lastJsonMessage,
    openTeleport,
    closeTeleport,
    gameliftStreamsSessionKey,
    isDebugMode,
    isTeleportFeatureEnabled,
  ]);
};

export const useHandleAvatarWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const timerIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { currentSkin, currentEvent, gameliftStreamsSessionKey } =
    useMytaverseEvent();
  const { sendMessageToUnreal, customAvatarUrl, isDebugMode } = useMytaverse();
  const { showNotification } = useNotification();
  const { t: translate } = useTranslation();

  const avatarMessage = useMemo(() => {
    const currentAvatarType = currentSkin
      ? currentEvent?.avatars?.find(
          (avatar) => avatar.avatarId === currentSkin.avatarId,
        )?.type
      : '';

    return {
      action: AvatarSelect,
      avatarId: currentSkin ? currentSkin.avatarId : '',
      type: currentAvatarType,
      skinId: currentSkin ? currentSkin.avatarSkinId : '',
      downloadAvatarUrl: customAvatarUrl || '',
      avatarType: customAvatarUrl ? 'AVATURN' : '',
    };
  }, [currentSkin, currentEvent, customAvatarUrl]);

  const sendAvatarMessage = useCallback(() => {
    sendMessageToUnreal(avatarMessage);
    MytaverseLogger.log(`SELECT_AVATAR: ${JSON.stringify(avatarMessage)}`);
  }, [sendMessageToUnreal, avatarMessage]);

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, data, sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    const handleMessage = () => {
      switch (action) {
        case AvatarRequested: {
          if (!timerIdRef.current) {
            timerIdRef.current = setInterval(sendAvatarMessage, 1000);
          }

          break;
        }
        case AvatarReceived: {
          if (timerIdRef.current) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = null;
          }
          break;
        }
        case UEError: {
          if (data?.category === WebSocketUECategory.UEAvatarLoadError) {
            showNotification(
              getNotification({
                message: translate(`notifications.ue.loadDefaultAvatar`),
                type: NOTIFICATION_TYPES.WARNING,
              }),
            );
          }
          break;
        }
      }
    };

    handleMessage();
  }, [
    lastJsonMessage,
    sendAvatarMessage,
    gameliftStreamsSessionKey,
    isDebugMode,
  ]);

  useEffect(() => {
    return () => {
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, []);
};

export const useHandleUELogWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { gameliftStreamsSessionKey } = useMytaverseEvent();
  const { isDebugMode } = useMytaverse();

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, data, sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    if (!action || !action.startsWith('UE_')) {
      return;
    }

    const msg = `UE: ${data?.category} - ${JSON.stringify(data?.msg || '')}`;

    switch (action) {
      case UEError:
        MytaverseLogger.error(msg);
        break;
      case UEWarn:
        MytaverseLogger.warn(msg);
        break;
      case UEInfo:
        MytaverseLogger.info(msg);
        break;
      case UEDebug:
        MytaverseLogger.debug(msg);
        break;
      default:
        MytaverseLogger.info(msg);
        break;
    }
  }, [lastJsonMessage, gameliftStreamsSessionKey, isDebugMode]);
};

export { useHandleEventSettingsMsg } from './useHandleEventSettingsMsg';
